import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { format } from "date-fns";
import { createDownloadBlob, useAccessToken, useStandardErrorHandler } from "modules/common";
import { toast } from "react-toastify";
import {
    createOrUpdateNotificationTemplate,
    CreateOrUpdateNotificationTemplateView,
    deleteNotificationTemplate,
    deleteUserNotifications,
    exportNotificationRecipients,
    ExportNotificationRecipientsRequest,
    getDeliveryStatisticsCounty,
    getDeliveryStatisticsSettlement,
    getNotificationTemplate,
    getNotificationTemplateAuthors,
    getNotificationTemplates,
    GetNotificationTemplatesRequest,
    getRecipientsCountByCounty,
    getRecipientsCountByIntervention,
    getRecipientsCountByRegistry,
    getRecipientsCountBySettlement,
    getRecipientsStatistics,
    getSentNotification,
    getSentNotifications,
    GetSentNotificationsRequest,
    GetUserNotificaitonsRequest,
    getUserNotification,
    getUserNotifications,
    highlightUserNotification,
    markNotificationRead,
    markNotificationsRead,
    NotificationDeliveryStatisticsRequest,
    NotificationDeliveryStatisticsResponse,
    NotificationRecipientsStatisticsResponse,
    NotificationStatisticsFilter,
    NotificationStatisticsResponse,
    NotificationTemplate,
    NotificationTemplateAuthorResponse,
    NotificationTemplatesResponse,
    NotificationView,
    sendNotification,
    SendNotificationRequest,
    SentNotificationsResponse,
    UserNotificationsResponse,
    UserNotificationView,
} from "services/communication";

// ---------------------
// | SEND NOTIFICATION |
// ---------------------
export const useSendNotification = () => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška prilikom slanja poruke");

    return useMutation({
        mutationFn: (data: SendNotificationRequest) => sendNotification(data, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            toast.success("Poruka je uspješno poslana na slanje");
        },
    });
};

export const useGetSentNotification = (notificationId: string, enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška prilikom dohvaćanja poslanih poruka");

    return useQuery<AxiosResponse<NotificationView>, Error, NotificationView>({
        queryKey: ["sentNotification", notificationId],
        queryFn: () => getSentNotification(notificationId, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useGetSentNotifications = (data: GetSentNotificationsRequest, enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška prilikom dohvaćanja poslanih poruka");

    return useQuery<AxiosResponse<SentNotificationsResponse>, Error, SentNotificationsResponse>({
        queryKey: ["sentNotifications", data],
        queryFn: () => getSentNotifications(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

// ---------------------------
// | NOTIFICATION STATISTICS |
// ---------------------------
export const useGetRecipientsCountByCounty = (
    data: NotificationStatisticsFilter,
    enabled = true
) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška prilikom broja korisnika po županiji");

    return useQuery<
        AxiosResponse<NotificationStatisticsResponse>,
        Error,
        NotificationStatisticsResponse
    >({
        queryKey: ["recipientsCountByCounty", data],
        queryFn: () => getRecipientsCountByCounty(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useGetRecipientsCountBySettlement = (
    data: NotificationStatisticsFilter,
    enabled = true
) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška prilikom broja korisnika po mjestu");

    return useQuery<
        AxiosResponse<NotificationStatisticsResponse>,
        Error,
        NotificationStatisticsResponse
    >({
        queryKey: ["recipientsCountBySettlement", data],
        queryFn: () => getRecipientsCountBySettlement(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useGetRecipientsCountByRegistry = (
    data: NotificationStatisticsFilter,
    enabled = true
) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler(
        "Pogreška prilikom dohvaćanja broja primatelja po registru"
    );

    return useQuery<
        AxiosResponse<NotificationStatisticsResponse>,
        Error,
        NotificationStatisticsResponse
    >({
        queryKey: ["recipientsCountByRegistry", data],
        queryFn: () => getRecipientsCountByRegistry(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useGetRecipientsCountByIntervention = (
    data: NotificationStatisticsFilter,
    enabled = true
) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler(
        "Pogreška prilikom dohvaćanja broja korisnika po intervenciji"
    );

    return useQuery<
        AxiosResponse<NotificationStatisticsResponse>,
        Error,
        NotificationStatisticsResponse
    >({
        queryKey: ["recipientsCountByIntervention", data],
        queryFn: () => getRecipientsCountByIntervention(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useGetRecipientsStatistics = (data: NotificationStatisticsFilter, enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler(
        "Pogreška prilikom dohvaćanja statistike korisnika"
    );

    return useQuery<
        AxiosResponse<NotificationRecipientsStatisticsResponse>,
        Error,
        NotificationRecipientsStatisticsResponse
    >({
        queryKey: ["recipientsCountByIntervention", data],
        queryFn: () => getRecipientsStatistics(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useGetDeliveryStatisticsCounty = (
    data: NotificationDeliveryStatisticsRequest,
    enabled = true
) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler(
        "Pogreška prilikom dohvaćanja statistike isporučenosti i pročitanosti"
    );

    return useQuery<
        AxiosResponse<NotificationDeliveryStatisticsResponse>,
        Error,
        NotificationDeliveryStatisticsResponse
    >({
        queryKey: ["deliveryStatisticsCounty", data.notificationId],
        queryFn: () => getDeliveryStatisticsCounty(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useGetDeliveryStatisticsSettlement = (
    data: NotificationDeliveryStatisticsRequest,
    enabled = true
) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler(
        "Pogreška prilikom dohvaćanja statistike isporučenosti i pročitanosti"
    );

    return useQuery<
        AxiosResponse<NotificationDeliveryStatisticsResponse>,
        Error,
        NotificationDeliveryStatisticsResponse
    >({
        queryKey: ["deliveryStatisticsSettlement", data.notificationId],
        queryFn: () => getDeliveryStatisticsSettlement(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

// --------------------------
// | NOTIFICATION TEMPLATES |
// --------------------------
export const useGetNotificationTemplate = (notificationTemplateId: string, enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška pri dohvaćanju predloška");

    return useQuery<
        AxiosResponse<{ notificationTemplate: NotificationTemplate }>,
        Error,
        NotificationTemplate
    >({
        queryKey: ["messageTemplate", notificationTemplateId],
        queryFn: () => getNotificationTemplate(notificationTemplateId, accessToken!),
        enabled,
        onError: errorHandler,
        select: response => response.data.notificationTemplate,
    });
};

export const useGetNotificationTemplates = (
    data: GetNotificationTemplatesRequest,
    enabled = true
) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška pri dohvaćanju predložaka");

    return useQuery<
        AxiosResponse<NotificationTemplatesResponse>,
        Error,
        NotificationTemplatesResponse
    >({
        queryKey: ["messageTemplates", data],
        queryFn: () => getNotificationTemplates(data, accessToken!),
        enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useCreateOrUpdateNotificationTemplate = () => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška pri spremanju predloška");
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateOrUpdateNotificationTemplateView) =>
            createOrUpdateNotificationTemplate(data, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["messageTemplates"]);
            toast.success("Predložak je uspješno spremljen");
        },
    });
};

export const useDeleteNotificationTemplate = () => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška pri brisanju predloška");

    return useMutation({
        mutationFn: (notificationTemplateId: string) =>
            deleteNotificationTemplate(notificationTemplateId, accessToken!),
        onError: errorHandler,
        onSuccess: () => toast.success("Predložak je uspješno obrisan"),
    });
};

export const useGetNotificationTemplateAuthors = (enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška pri dohvaćanju autora predložaka");

    return useQuery<
        AxiosResponse<{ notificationTemplateAuthors: NotificationTemplateAuthorResponse[] }>,
        Error,
        NotificationTemplateAuthorResponse[]
    >({
        queryKey: ["templateAuthors"],
        queryFn: () => getNotificationTemplateAuthors(accessToken!),
        onError: errorHandler,
        enabled: enabled,
        select: response => response.data.notificationTemplateAuthors,
    });
};

export const exportRecipients = async (
    data: ExportNotificationRecipientsRequest,
    accessToken: string | undefined
) => {
    const reponse = await exportNotificationRecipients(data, accessToken!);
    createDownloadBlob(reponse, `ListaPrimatelja_${format(new Date(), "ddMMyyy")}`);
};

// ---------------------
// | USER NOTIFICATION |
// ---------------------
export const useGetUserNotification = (userNotificationId: string, enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška prilikom dohvaćanja poslanih poruka");
    const queryClient = useQueryClient();

    return useQuery<
        AxiosResponse<{ userNotification: UserNotificationView }>,
        Error,
        UserNotificationView
    >({
        queryKey: ["userNotification", userNotificationId],
        queryFn: () => getUserNotification(userNotificationId, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        onSuccess: () => queryClient.invalidateQueries(["userNotifications"]),
        select: response => response.data.userNotification,
    });
};

export const useGetUserNotifications = (data: GetUserNotificaitonsRequest, enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška prilikom dohvaćanja poslanih poruka");

    return useQuery<AxiosResponse<UserNotificationsResponse>, Error, UserNotificationsResponse>({
        queryKey: ["userNotifications", data],
        queryFn: () => getUserNotifications(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useHighlightUserNotification = () => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška pri izdvajanju poruke");
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (userNotificationId: string) =>
            highlightUserNotification(userNotificationId, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["userNotifications"]);
            queryClient.invalidateQueries(["userNotification"]);
        },
    });
};

export const useDeleteUserNotifications = () => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška pri brisanju poruka");
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (userNotificationIds: string[]) =>
            deleteUserNotifications({ userNotificationIds }, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["userNotification"]);
            queryClient.invalidateQueries(["userNotifications"]);
            toast.success("Poruke su uspješno obrisane");
        },
    });
};

export const useMarkNotificationRead = () => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Pogreška pri označavanju poruke pročitanom");

    return useMutation({
        mutationFn: (notificationTemplateId: string) =>
            markNotificationRead(notificationTemplateId, accessToken!),
        onError: errorHandler,
        onSuccess: () => queryClient.invalidateQueries(["userNotifications"]),
    });
};

export const useMarkNotificationsRead = () => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Pogreška pri označavanju poruka pročitanim");
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (notificationTemplateIds: string[]) =>
            markNotificationsRead(notificationTemplateIds, accessToken!),
        onError: errorHandler,
        onSuccess: () => queryClient.invalidateQueries(["userNotifications"]),
    });
};
